import { useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiUpload, FiLoader, FiX } from 'react-icons/fi'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

export default function PublicResumeUpload({ website, section, sectionFormStyling, useDefaultStyling = true, sectionType = 'resume' }) {
  const [formData, setFormData] = useState({
    visitorName: '',
    visitorEmail: '',
    visitorPhone: '',
    position: '',
    coverLetter: ''
  })
  const [resumeFile, setResumeFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [isFormHovered, setIsFormHovered] = useState(false)
  const [isButtonHovered, setIsButtonHovered] = useState(false)

  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const hoverFormStyle = isFormStyling ? {
    backgroundColor: formColors?.hoverBackgroundColor || formColors?.backgroundColor || DEFAULT_FORM_COLORS.hoverBackgroundColor,
    color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor
  } : undefined
  const labelStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const hoverLabelStyle = isFormStyling ? { color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const hoverTextStyle = isFormStyling ? { color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor } : undefined
  const inputStyle = isFormStyling ? { 
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor,
    borderColor: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  const hoverButtonStyle = isButtonStyling ? {
    backgroundColor: formColors?.hoverButtonBackgroundColor || formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.hoverButtonBackgroundColor,
    color: formColors?.hoverButtonTextColor || formColors?.buttonTextColor || DEFAULT_FORM_COLORS.hoverButtonTextColor
  } : { backgroundColor: '#2563EB', color: '#FFFFFF' }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain']
      
      if (!validTypes.includes(file.type)) {
        toast.error('Only PDF, DOC, DOCX, and TXT files are allowed')
        return
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB')
        return
      }

      setResumeFile(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (!formData.visitorName.trim()) {
      toast.error('Name is required')
      return
    }
    if (!formData.visitorEmail.trim()) {
      toast.error('Email is required')
      return
    }
    if (!formData.position.trim()) {
      toast.error('Position is required')
      return
    }
    if (!resumeFile) {
      toast.error('Please select a resume file')
      return
    }

    try {
      setUploading(true)

      const submitFormData = new FormData()
      submitFormData.append('visitorName', formData.visitorName)
      submitFormData.append('visitorEmail', formData.visitorEmail)
      submitFormData.append('visitorPhone', formData.visitorPhone)
      submitFormData.append('position', formData.position)
      submitFormData.append('coverLetter', formData.coverLetter)
      submitFormData.append('resume', resumeFile)

      const response = await axios.post(
        `/resumes/website/${website._id}`,
        submitFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      console.log('[PublicResumeUpload] Resume submitted:', response.data)
      setSubmitted(true)
      toast.success('Resume submitted successfully! We will review it shortly.')
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          visitorName: '',
          visitorEmail: '',
          visitorPhone: '',
          position: '',
          coverLetter: ''
        })
        setResumeFile(null)
        setSubmitted(false)
      }, 3000)
    } catch (error) {
      console.error('[PublicResumeUpload] Error:', error)
      toast.error(error.response?.data?.message || 'Failed to submit resume')
    } finally {
      setUploading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <div className="text-center bg-green-50 border border-green-200 rounded-lg p-8 max-w-md">
          <div className="text-4xl mb-4">✅</div>
          <h3 className="text-2xl font-bold mb-2" style={textStyle}>Resume Submitted!</h3>
          <p style={textStyle}>
            Thank you for your interest! We will review your resume and contact you shortly.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <form 
        onSubmit={handleSubmit} 
        className="space-y-6 rounded-lg shadow p-8 transition-colors duration-200"
        style={isFormHovered && hoverFormStyle ? hoverFormStyle : formStyle}
        onMouseEnter={() => setIsFormHovered(true)}
        onMouseLeave={() => setIsFormHovered(false)}
      >
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold" style={textStyle}>📋 Personal Information</h3>

          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>
              Full Name *
            </label>
            <input
              type="text"
              name="visitorName"
              value={formData.visitorName}
              onChange={handleInputChange}
              placeholder="Your full name"
              style={inputStyle}
              className="input-field w-full"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>
                Email Address *
              </label>
              <input
                type="email"
                name="visitorEmail"
                value={formData.visitorEmail}
                onChange={handleInputChange}
                placeholder="your@email.com"
                style={inputStyle}
                className="input-field w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>
                Phone Number
              </label>
              <input
                type="tel"
                name="visitorPhone"
                value={formData.visitorPhone}
                onChange={handleInputChange}
                placeholder="+1 (555) 000-0000"
                style={inputStyle}
                className="input-field w-full"
              />
            </div>
          </div>
        </div>

        {/* Position Information */}
        <div className="space-y-4 border-t pt-6">
          <h3 className="text-lg font-semibold" style={textStyle}>💼 Position Information</h3>

          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>
              Position Applied For *
            </label>
            <input
              type="text"
              name="position"
              value={formData.position}
              onChange={handleInputChange}
              placeholder="e.g., Software Engineer, Data Analyst"
              style={inputStyle}
              className="input-field w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>
              Cover Letter
            </label>
            <textarea
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleInputChange}
              placeholder="Tell us why you're a great fit for this position..."
              rows="5"
              style={inputStyle}
              className="input-field w-full resize-none"
            />
          </div>
        </div>

        {/* Resume Upload */}
        <div className="space-y-4 border-t pt-6">
          <h3 className="text-lg font-semibold" style={textStyle}>📄 Resume Upload</h3>

          <div className="relative">
            <input
              type="file"
              id="resumeFile"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.txt"
              className="hidden"
              required
            />

            <label
              htmlFor="resumeFile"
              className="flex items-center justify-center w-full px-6 py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <div className="text-center">
                {resumeFile ? (
                  <>
                    <FiUpload className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-700">{resumeFile.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-primary-600">
                        Click to upload
                      </span>
                      {' '}or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, DOC, DOCX, or TXT up to 5MB
                    </p>
                  </>
                )}
              </div>
            </label>
          </div>

          {resumeFile && (
            <button
              type="button"
              onClick={() => setResumeFile(null)}
              className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            >
              <FiX className="w-4 h-4" />
              Remove File
            </button>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex gap-4 pt-6 border-t">
          <button
            type="submit"
            disabled={uploading}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold hover:bg-primary-700 disabled:bg-gray-400 rounded-lg transition-colors duration-200"
            style={isButtonHovered && hoverButtonStyle ? hoverButtonStyle : buttonStyle}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            {uploading ? (
              <>
                <FiLoader className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <FiUpload className="w-5 h-5" />
                Submit Resume
              </>
            )}
          </button>
        </div>

        {/* Info Box */}
    
          <p className="text-sm" style={textStyle}>
            ℹ️ After submitting your resume, our team will review it and contact you at the email address provided if you're a good match for the position.
          </p>
       
      </form>
    </div>
  )
}
